import { useState } from 'react'

import { BlankSlate, Box, Button, Text, useToggle } from '@factorialco/deprecated-design-system'
import {
  ProjectionKind,
  useRateConfigurationsDatatable,
} from 'generated/resources/datatable.graphql'
import { RateConfigurationDocument } from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import { getCurrencySymbolFromCode } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'

import ArchiveRateModal from 'modules/expenses/pages/settings/RateConfigurationsPage/ArchiveRateModal'
import RateFormModal from 'modules/expenses/pages/settings/RateConfigurationsPage/RateFormModal'
import {
  ExpensableRateConfigurationResourceType,
  ExpensableRateConfigurationResourceTypeEnum,
} from 'modules/expenses/types/Expensable'
import { amountWithCurrency } from 'modules/finance/lib/budgets'

import { CurrencyFilter, EmployeeFilter, LegalEntityFilter } from './_Filters'

interface Props {
  archivedOnly?: boolean
  resourceType: ExpensableRateConfigurationResourceType
  onClickAddRate: () => void
}

const RateConfigurationsDataTable = ({ archivedOnly, onClickAddRate, resourceType }: Props) => {
  const { navigation } = useNavigation()
  const {
    company,
    access: { locale },
  } = useCurrentSession()

  const { debouncedSearch, search, onSearch } = useDebouncedSearch()
  const [showEditModal, toggleEditModal] = useToggle(false)
  const [showArchiveModal, toggleArchiveModal] = useToggle(false)
  const [selectedId, setSelectedId] = useState<string>()

  const { data, loading } = useQuery(RateConfigurationDocument, {
    variables: { companyId: company.id, id: String(selectedId) },
    skip: !selectedId,
  })
  const selectedRate = data?.expenses?.rateConfigurationsConnection?.nodes[0]

  const handleEdit = (id: string) => {
    setSelectedId(id)
    toggleEditModal(true)()
  }
  const handleArchive = (id: string) => {
    setSelectedId(id)
    toggleArchiveModal(true)()
  }

  const source = useRateConfigurationsDatatable(
    [
      {
        identifier: 'name',
        title: i18n.t('expenses_v2.settings.rate_configurations.datatable.fields.name'),
        kind: ProjectionKind.Custom,
        sortBy: 'name',
        template: '',
        colSpan: 1,
        render: ({ name }) => (
          <Box flexDirection="row" alignItems="center" gap="s16">
            <Text.Normal weight="medium">{name}</Text.Normal>
          </Box>
        ),
      },
      {
        identifier: 'amountCents',
        title:
          resourceType === ExpensableRateConfigurationResourceTypeEnum.PerDiem
            ? i18n.t('expenses_v2.settings.rate_configurations.datatable.fields.amount')
            : i18n.t('expenses_v2.settings.rate_configurations.datatable.fields.rate'),
        kind: ProjectionKind.Custom,
        sortBy:
          resourceType === ExpensableRateConfigurationResourceTypeEnum.PerDiem
            ? 'amountCents'
            : 'rate',
        template: '',
        colSpan: 1,
        render: ({ amountCents, currency, rate }) => {
          if (resourceType === ExpensableRateConfigurationResourceTypeEnum.PerDiem) {
            return (
              <Text.Normal weight="medium">
                {amountWithCurrency(Number(amountCents), currency, locale)}
              </Text.Normal>
            )
          } else {
            return (
              <Text.Normal weight="medium">
                {rate} {getCurrencySymbolFromCode(currency)}
              </Text.Normal>
            )
          }
        },
      },
      {
        identifier: 'assignees',
        title: i18n.t(
          'expenses_v2.settings.rate_configurations.create_modal.fields.assignees.title'
        ),
        kind: ProjectionKind.Custom,
        template: '',
        colSpan: 1,
        render: ({ rateAssigneesConnection }) => (
          <Text.Normal weight="medium" ellipsis>
            {rateAssigneesConnection.totalCount}
          </Text.Normal>
        ),
      },
      {
        identifier: 'legalEntities',
        title: i18n.t('expenses_v2.settings.rate_configurations.datatable.fields.legal_entity'),
        kind: ProjectionKind.Custom,
        template: '',
        colSpan: 2,
        render: ({ legalEntitiesConnection }) => {
          const legalNames = legalEntitiesConnection.nodes.map((x) => x.legalName).join(', ')
          return (
            <Text.Normal weight="medium" ellipsis>
              {legalNames}
            </Text.Normal>
          )
        },
      },
      {
        identifier: 'archivedAt',
        title: i18n.t('expenses_v2.settings.rate_configurations.datatable.fields.archived_at'),
        kind: ProjectionKind.Custom,
        sortBy: 'archivedAt',
        template: '',
        colSpan: 2,
        hidden: !archivedOnly,
        render: ({ archivedAt }) => {
          return <Text.Normal weight="medium">{dateBackendDateTimeToUI(archivedAt)}</Text.Normal>
        },
      },
      {
        identifier: 'actions',
        title: '',
        kind: ProjectionKind.Custom,
        template: '',
        hidden: archivedOnly,
        render: ({ id }) => (
          <Box flexDirection="row" justifyContent="center" gap="s8">
            <PopoverMenu type="list">
              <Box flexDirection="row" justifyContent="flexEnd">
                <PopoverMenu.Trigger>
                  <Button.Decorative type="secondary" iconName="ellipsis" />
                </PopoverMenu.Trigger>
              </Box>
              <PopoverMenu.Content side="bottom" align="center">
                <PopoverMenu.TouchableOption
                  icon="pencil"
                  title={i18n.t('expenses_v2.settings.rate_configurations.datatable.actions.edit')}
                  onClick={() => handleEdit(id)}
                  disabled={loading}
                />
                <PopoverMenu.TouchableOption
                  icon="archive"
                  title={i18n.t(
                    'expenses_v2.settings.rate_configurations.datatable.actions.archive'
                  )}
                  onClick={() => handleArchive(id)}
                  disabled={loading}
                />
              </PopoverMenu.Content>
            </PopoverMenu>
            <Button.Router
              type="secondary"
              to={
                resourceType === ExpensableRateConfigurationResourceTypeEnum.PerDiem
                  ? navigation.settings.expenses.rateConfigurations.rateId.fullPath({ rateId: id })
                  : navigation.settings.expenses.mileageRates.rateId.fullPath({ rateId: id })
              }
              iconName="arrow-right"
            />
          </Box>
        ),
      },
    ],
    {
      variables: { companyId: company.id, search: debouncedSearch, archivedOnly, resourceType },
    }
  )
  const filters = [LegalEntityFilter(), CurrencyFilter(), EmployeeFilter()]

  return (
    <>
      <DataTable.Provider
        source={source}
        sort={{ path: 'id', direction: 'desc' }}
        filters={filters}
        pagination={{
          kind: 'cursor',
          itemsPerPage: 25,
          infiniteScroll: false,
        }}
      >
        <Box gap="s16" paddingTop="s16">
          <DataTable.HeadBar
            disableExport
            actions={
              <Button iconName="add" onClick={() => onClickAddRate()}>
                {i18n.t('expenses_v2.settings.rate_configurations.add_rate')}
              </Button>
            }
            search={{
              placeholder: i18n.t('common.search'),
              value: search,
              onChange: onSearch,
            }}
          />
          <DataTable.Table
            emptyState={
              <BlankSlate
                illustration="wireframe"
                description={i18n.t('common.no_data_available')}
              />
            }
          />
        </Box>
      </DataTable.Provider>

      {selectedRate && showEditModal && (
        <RateFormModal
          title={
            resourceType === ExpensableRateConfigurationResourceTypeEnum.PerDiem
              ? i18n.t('expenses_v2.settings.rate_configurations.edit_modal.title')
              : i18n.t('expenses_v2.settings.rate_configurations.edit_modal.mileage_title')
          }
          description={i18n.t('expenses_v2.settings.rate_configurations.edit_modal.description')}
          rateConfiguration={selectedRate}
          onClose={toggleEditModal(false)}
          resourceType={resourceType}
        />
      )}
      {selectedRate && showArchiveModal && (
        <ArchiveRateModal rateConfiguration={selectedRate} onClose={toggleArchiveModal(false)} />
      )}
    </>
  )
}

export default RateConfigurationsDataTable
